import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { setBingMap as setBingMapAction } from "@/features/survey/surveySlice";
import { useGetEmeraldBingTilesQuery } from "@/services/bingApi";
import { useGetLeaseCompsQuery } from "@/services/leaseComps";
import { useGetPolygonsQuery } from "@/services/survey-polygons";
import { useGetPOIsQuery, useGetPOICategoriesQuery } from "@/services/survey-poi";
import { useWindowVisitTracking } from "@/shared/hooks/useWindowVisitTracking";
import NotFoundPage from "@components/NotFoundPage";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  useGetSurveyQuery,
  useLazyGetSurveyQuery,
} from "@services/survey-core";
import BuilderHeader from "@survey-builder/BuilderHeader";
import BuilderMain from "@survey-builder/BuilderMain";
import BuilderSidebar from "@survey-builder/BuilderSidebar";
import InvalidCoordinatesModal from "@survey-builder/InvalidCoordinatesModal";
import { useEffect, useEffectEvent } from "react";
import { useParams } from "react-router";

const Builder = () => {
  const { surveyId } = useParams();
  const isPropertiesDrawerOpen = useAppSelector(
    (state) => state.survey.isPropertiesDrawerOpen,
  );
  const isPOIsDrawerOpen = useAppSelector(
    (state) => state.survey.isPOIsDrawerOpen,
  );
  const isLeaseCompsDrawerOpen = useAppSelector(
    (state) => state.leaseComps.isDrawerOpen,
  );
  const dispatch = useAppDispatch();
  const [fetchSurvey] = useLazyGetSurveyQuery();
  const bingMap = useAppSelector((state) => state.survey.currentBingMap);

  // Initialize window visit tracking - tracks time since last visit and sets refetchOnFocus flag
  // RTK Query APIs (surveyApi, userApi, mediaApi) use this flag to refetch when data is stale
  useWindowVisitTracking();

  // RTK query with selectFromResult for optimized rerenders
  const surveyQuery = useGetSurveyQuery(surveyId ?? skipToken, {
    refetchOnReconnect: true,
    selectFromResult: ({ data, ...rest }) => ({
      ...rest,
      data,
      properties: data?.properties,
    }),
  });

  // Fetch lease comps data if this is a lease comps comparison
  useGetLeaseCompsQuery(surveyId || "", {
    skip: !surveyId,
    refetchOnMountOrArgChange: false,
  });

  // Fetch POI categories and data at top level to avoid duplicate requests from child components
  useGetPOICategoriesQuery(surveyId || "", {
    skip: !surveyId,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
  });

  // Fetch POI data for count display in header
  useGetPOIsQuery(surveyId || "", {
    skip: !surveyId,
    refetchOnMountOrArgChange: false,
  });

  // Fetch polygons for map preview/rendered view
  useGetPolygonsQuery(surveyId || "", {
    skip: !surveyId,
    refetchOnMountOrArgChange: false,
  });

  const { data: bingTilesData } = useGetEmeraldBingTilesQuery();

  const handleBingMapSetup = useEffectEvent(() => {
    if (!bingMap && bingTilesData) {
      dispatch(setBingMapAction(bingTilesData));
    }
  });

  useEffect(() => {
    handleBingMapSetup();
  }, [bingTilesData]);
  useEffect(() => {
    // Sync survey name with store on mount and when survey changes
    if (!isPropertiesDrawerOpen && !isPOIsDrawerOpen && !isLeaseCompsDrawerOpen) {
      fetchSurvey(surveyId!); // Refetch survey to sync any property/POI/lease comp changes
    }
  }, [isPropertiesDrawerOpen, isPOIsDrawerOpen, isLeaseCompsDrawerOpen, surveyId]);
  // Get error details based on status code
  const getErrorDetails = () => {
    if (!surveyQuery.error) {
      return {
        title: "Survey Not Found",
        description:
          "The survey you are looking for does not exist or you don't have access to it.",
      };
    }

    const error = surveyQuery.error as { status?: number; data?: unknown };
    const status = error.status;

    switch (status) {
      case 403:
        return {
          title: "Access Denied",
          description:
            "You don't have permission to access this survey. Please contact your administrator.",
        };
      case 404:
        return {
          title: "Survey Not Found",
          description:
            "The survey you are looking for does not exist. It may have been deleted or moved.",
        };
      case 500:
        return {
          title: "Server Error",
          description:
            "We're experiencing technical difficulties. Please try again later or contact support.",
        };
      case 503:
        return {
          title: "Service Unavailable",
          description:
            "The service is temporarily unavailable. Please try again in a few moments.",
        };
      default:
        return {
          title: "Unable to Load Survey",
          description:
            "An unexpected error occurred while loading the survey. Please try again.",
        };
    }
  };

  const errorDetails = getErrorDetails();

  return (
    <>
      {surveyQuery.isError ? (
        <NotFoundPage
          title={errorDetails.title}
          description={errorDetails.description}
        />
      ) : (
        <section className="builder-content">
          <BuilderHeader />
          <section className="builder-body">
            <BuilderSidebar />
            <BuilderMain />
          </section>
        </section>
      )}
      <InvalidCoordinatesModal />
    </>
  );
};

export default Builder;
